import type {
  LogbookApproaches,
  LogbookClass,
  IsoInstant,
  LocalDate,
  LogbookEntry,
  LogbookEntryEdit,
  LogbookLandings,
  LogbookTakeoffs,
  LogbookTimeMatrix,
} from '@/lib/db';

import { columnById, LOGBOOK_COLUMNS } from './columns';

/**
 * Building and editing entries. `columns.ts` knows how to read and write one
 * cell; this is where an edit becomes a whole new entry — with history kept,
 * per `NZ-LOGBOOK.md` §2.4, and the derived-field badge dropped the moment a
 * pilot types over a value the track supplied.
 */

export function blankTimeMatrix(
  engineClass: LogbookClass = 'single-engine-land',
): LogbookTimeMatrix {
  return {
    engineClass,
    dayHr: 0,
    nightHr: 0,
    picHr: 0,
    commandPracticeHr: 0,
    dualHr: 0,
    coPilotHr: 0,
    instructorHr: 0,
    actualInstrumentHr: 0,
    simulatedInstrumentHr: 0,
    groundInstrumentHr: 0,
    ifrCrossCountryHr: 0,
  };
}

export function blankApproaches(): LogbookApproaches {
  return { nonPrecision: 0, precision: 0 };
}

export function blankLandings(): LogbookLandings {
  return { day: 0, night: 0, touchAndGo: 0 };
}

export function blankTakeoffs(): LogbookTakeoffs {
  return { day: 0, night: 0 };
}

export interface NewEntryInput {
  flightId: string;
  date: LocalDate;
  aircraftId?: string;
  registration?: string;
  aircraftType?: string;
  pic?: string;
  engineClass?: LogbookClass;
}

/**
 * A blank row the pilot fills in by hand — no track, nothing derived. Starts
 * as a draft like any other entry, not because there's anything to review,
 * but because an approved entry is locked (`applyCellEdit`): a brand-new row
 * has to stay editable until the pilot has actually typed something into it.
 */
export function createManualEntry(
  input: NewEntryInput,
  now: IsoInstant = new Date().toISOString(),
): LogbookEntry {
  return {
    id: crypto.randomUUID(),
    flightId: input.flightId,
    date: input.date,
    ...(input.aircraftId === undefined ? {} : { aircraftId: input.aircraftId }),
    ...(input.registration === undefined ? {} : { registration: input.registration }),
    ...(input.aircraftType === undefined ? {} : { aircraftType: input.aircraftType }),
    pic: input.pic ?? 'SELF',
    time: blankTimeMatrix(input.engineClass),
    approaches: blankApproaches(),
    landings: blankLandings(),
    takeoffs: blankTakeoffs(),
    isDraft: true,
    derivedFields: [],
    history: [],
    createdAt: now,
    updatedAt: now,
  };
}

export interface CellEditResult {
  ok: boolean;
  entry?: LogbookEntry;
}

/**
 * Apply one typed cell value. Records the previous value in `history`, and —
 * this is the point of the function, not a side effect — drops the field from
 * `derivedFields` so a track-sourced number a pilot has looked at and kept
 * reads as confirmed rather than merely inherited.
 *
 * An approved entry is locked: `confirmEntry` is what makes it count toward
 * totals and currency, and a field that could still change under it would
 * make that count a moving target. `unconfirmEntry` is the one door back in —
 * put it in review, edit, re-approve.
 */
export function applyCellEdit(
  entry: LogbookEntry,
  columnId: string,
  raw: string,
  now: IsoInstant = new Date().toISOString(),
): CellEditResult {
  if (!entry.isDraft) {
    return { ok: false };
  }
  const column = columnById(columnId);
  if (column === undefined) {
    return { ok: false };
  }
  const previousRaw = column.read(entry);
  const written = column.write(entry, raw);
  if (written === undefined) {
    return { ok: false };
  }
  if (column.read(written) === previousRaw) {
    return { ok: true, entry };
  }

  const history = [...entry.history, { at: now, field: columnId, previousValue: previousRaw }];
  const derivedFields = entry.derivedFields.filter((field) => field !== columnId);
  return { ok: true, entry: { ...written, history, derivedFields, updatedAt: now } };
}

/**
 * Apply one value the *track* supplied, rather than one the pilot typed.
 *
 * The mirror image of `applyCellEdit`, and the difference is the whole point.
 * An edit means "I have looked at this and it is mine now", so the field drops
 * out of `derivedFields`. This means "take what the track says", so the field
 * joins it, and the grid keeps underlining the cell until a pilot types over
 * it. Both record the previous value in `history`, because `NZ-LOGBOOK.md`
 * §2.4's append-only habit does not care who changed it.
 *
 * Used by the API matching flow, where a pilot accepts a track's proposal for
 * a field they had left blank — or, deliberately chosen field by field, one
 * they had filled in differently.
 */
export function applyDerivedValue(
  entry: LogbookEntry,
  columnId: string,
  raw: string,
  now: IsoInstant = new Date().toISOString(),
): CellEditResult {
  if (!entry.isDraft) {
    return { ok: false };
  }
  const column = columnById(columnId);
  if (column === undefined) {
    return { ok: false };
  }
  const previousRaw = column.read(entry);
  const written = column.write(entry, raw);
  if (written === undefined) {
    return { ok: false };
  }
  if (column.read(written) === previousRaw) {
    // No change, but the value is the track's now rather than a coincidence,
    // so the badge still belongs on it.
    return { ok: true, entry: markDerived(entry, [...entry.derivedFields, columnId]) };
  }

  const history = [...entry.history, { at: now, field: columnId, previousValue: previousRaw }];
  return {
    ok: true,
    entry: markDerived({ ...written, history, updatedAt: now }, [...entry.derivedFields, columnId]),
  };
}

/** Marks every field currently populated by the linked track as derived, for a fresh draft. */
export function markDerived(entry: LogbookEntry, fields: readonly string[]): LogbookEntry {
  return { ...entry, derivedFields: [...new Set(fields)] };
}

export function confirmEntry(
  entry: LogbookEntry,
  now: IsoInstant = new Date().toISOString(),
): LogbookEntry {
  return { ...entry, isDraft: false, confirmedAt: now, updatedAt: now };
}

/** Puts a confirmed entry back into review — a pilot who approved too fast gets to undo that, not just the numbers. */
export function unconfirmEntry(
  entry: LogbookEntry,
  now: IsoInstant = new Date().toISOString(),
): LogbookEntry {
  const { confirmedAt: _confirmedAt, ...rest } = entry;
  return { ...rest, isDraft: true, updatedAt: now };
}

/**
 * The history an edit leaves behind, for a change made to several fields at
 * once.
 *
 * `applyCellEdit` records one keystroke in one cell. The flight form saves a
 * whole entry, and a logbook is an official record — `NZ-LOGBOOK.md` §2.4's
 * append-only habit does not stop applying because the pilot used a form
 * instead of the grid. So the two versions are compared through the column
 * list, and every column that now reads differently contributes the same
 * `LogbookEntryEdit` a cell edit would have.
 *
 * Reading through the columns rather than diffing the objects is what keeps
 * this honest: a column is what the pilot can see, and a `history` entry
 * naming a field with no column is a note nobody can act on. The three
 * fields the form owns and the grid has no column for — role, purpose and
 * the training details — are named explicitly for the same reason.
 *
 * Changed fields also drop out of `derivedFields`: a value a pilot has
 * typed over is theirs now, whatever the track originally said.
 */
export function recordChanges(
  before: LogbookEntry,
  after: LogbookEntry,
  now: IsoInstant = new Date().toISOString(),
): LogbookEntry {
  const changed: LogbookEntryEdit[] = [];

  for (const column of LOGBOOK_COLUMNS) {
    const previousValue = column.read(before);
    if (column.read(after) !== previousValue) {
      changed.push({ at: now, field: column.id, previousValue });
    }
  }
  for (const field of ['role', 'purpose', 'trainingDetails'] as const) {
    if (after[field] !== before[field]) {
      changed.push({ at: now, field, previousValue: before[field] ?? '' });
    }
  }

  if (changed.length === 0) {
    return before;
  }
  const touched = new Set(changed.map((edit) => edit.field));
  return {
    ...after,
    history: [...before.history, ...changed],
    derivedFields: before.derivedFields.filter((field) => !touched.has(field)),
    updatedAt: now,
  };
}
